import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { identityAPI, taxEngineAPI } from '../../services/api';
import {
  apiErrorMessage, demandAmountBreakdown, demandBalance, demandSubjectName, demandSubjectReference,
  demandTaxName, groupDemandsByAccount, groupDemandsByFinancialYear, groupTaxAccountsBySubject,
  isPayableDemand, makeIdempotencyKey, money, paymentAllocations, paymentModeLabel, summarizeDemands,
  receiptAllocationSummary, saveBlob, selectedPayableDemands, shortDate, statusLabel, subjectLabel, subjectTypeLabel,
} from '../tax/taxWorkspace';
import './TaxAccountWorkspacePage.css';
import { GoogleLocationMap } from '../../components/maps/LocationMap';
import TaxPdfViewer from '../../components/tax/TaxPdfViewer';
import TaxStatutoryDisclosure from '../../components/tax/TaxStatutoryDisclosure';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './TaxAccessIssues.css';
import '../tax/TaxDemandDocuments.css';

const CLAIM_RELATIONSHIPS = {
  business: ['owner', 'co_owner', 'partner', 'director', 'operator', 'authorized_representative', 'accountant', 'payer'],
  property: ['owner', 'co_owner', 'occupant', 'tenant', 'authorized_representative', 'payer'],
  tax_account: ['liable_party', 'authorized_representative', 'payer'],
};

const RELATIONSHIP_LABELS = {
  owner: 'मालक', co_owner: 'सहमालक', partner: 'भागीदार', director: 'संचालक',
  operator: 'संचालक प्रतिनिधी', authorized_representative: 'अधिकृत प्रतिनिधी',
  accountant: 'लेखापाल', payer: 'कर भरणारा', occupant: 'रहिवासी', tenant: 'भाडेकरू',
  liable_party: 'करदायी व्यक्ती',
};

const ISSUE_TYPE_LABELS = {
  missing_account: 'कर खाते दिसत नाही', incorrect_link: 'चुकीची जोडणी',
  incorrect_relationship: 'चुकीचे नाते', duplicate_record: 'दुबार नोंद', other: 'इतर समस्या',
};

const REGISTRY_REQUEST_LABELS = {
  new_property: 'नवीन किंवा आकारणी न झालेली मालमत्ता कळवा',
  new_business: 'नवीन व्यवसाय नोंदणीसाठी माहिती द्या',
  property_correction: 'मालमत्ता नोंदीतील दुरुस्ती',
  ownership_transfer: 'मालकी हस्तांतरण',
  inheritance: 'वारस नोंद',
  construction_change: 'नवीन किंवा वाढीव बांधकाम',
  demolition: 'पाडकाम किंवा नष्ट झालेली इमारत',
  business_correction: 'व्यवसाय नोंदीतील दुरुस्ती',
  business_closure: 'व्यवसाय बंद झाल्याची नोंद',
  other: 'इतर नोंदणी सहाय्य',
};

const loadRazorpay = () => new Promise(resolve => {
  if (window.Razorpay) return resolve(true);
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

const Notice = ({ type = 'info', children }) => children
  ? <div className={`tax-notice ${type}`} role={type === 'error' ? 'alert' : 'status'}>{children}</div>
  : null;

const TaxDocumentIcon = ({ name }) => {
  const paths = {
    view: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6S2.5 12 2.5 12Z" /><circle cx="12" cy="12" r="2.75" /></>,
    download: <><path d="M12 3v11" /><path d="m7.5 10 4.5 4.5 4.5-4.5" /><path d="M4 17.5V21h16v-3.5" /></>,
  };
  return <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[name]}</svg>;
};

const TaxDocumentSpinner = () => <span className="tax-document-spinner" aria-hidden="true" />;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const readableDocumentReference = value => {
  const text = String(value || '').trim();
  return text && !UUID_PATTERN.test(text) ? text : '';
};

const DemandPaymentDetails = ({ demand }) => <div className="tax-payment-history">
  {(demand.payments || []).map((payment, index) => <small key={`${payment.receipt_uid || payment.transaction_id || index}`}>
    <b>{money(payment.amount)}</b> · {shortDate(payment.paid_at)} · {paymentModeLabel(payment.payment_mode)}
    {(payment.provider === 'razorpay' || payment.payment_mode === 'online') && payment.transaction_id ? <> · व्यवहार क्र. <code>{payment.transaction_id}</code></> : null}
  </small>)}
</div>;

export default function TaxAccountWorkspacePage() {
  const { tenantSlug } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [demands, setDemands] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [exemptions, setExemptions] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [claims, setClaims] = useState([]);
  const [issues, setIssues] = useState([]);
  const [registryRequests, setRegistryRequests] = useState([]);
  const [selected, setSelected] = useState([]);
  const [confirmPay, setConfirmPay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [exemptionAccount, setExemptionAccount] = useState(null);
  const [appealDemand, setAppealDemand] = useState(null);
  const [claimForm, setClaimForm] = useState({ target_type: 'property', target_uid: '', requested_relationship: 'owner', note: '' });
  const [claimSearch, setClaimSearch] = useState({ query: '', results: [], loading: false, searched: false });
  const [issueForm, setIssueForm] = useState({ target_type: 'tax_account', target_uid: '', issue_type: 'incorrect_link', statement: '' });
  const [registryForm, setRegistryForm] = useState({ request_type: 'new_property', reference_number: '', statement: '' });
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [receiptPreviewLoading, setReceiptPreviewLoading] = useState(false);
  const [demandNoticePreview, setDemandNoticePreview] = useState(null);
  const [demandNoticePreviewLoading, setDemandNoticePreviewLoading] = useState(false);
  const [documentDownloadKey, setDocumentDownloadKey] = useState('');
  const [onlinePayment, setOnlinePayment] = useState({ available: false, loading: true, message: '' });

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      const results = await Promise.allSettled([
        taxEngineAPI.getMyAccounts(tenantSlug, { limit: 100 }),
        taxEngineAPI.getMyDemands(tenantSlug, { limit: 100 }),
        taxEngineAPI.getMyReceipts(tenantSlug, { limit: 100 }),
        taxEngineAPI.getMyExemptions(tenantSlug, { limit: 100 }),
        taxEngineAPI.getMyAppeals(tenantSlug, { limit: 100 }),
        identityAPI.getMyClaims(tenantSlug, { limit: 100 }),
        identityAPI.getMyIssues(tenantSlug, { limit: 100 }),
        taxEngineAPI.getPaymentAvailability(tenantSlug),
        identityAPI.getMyRegistryRequests(tenantSlug, { limit: 100 }),
      ]);
      const requiredFailure = results.slice(0, 3).find(item => item.status === 'rejected');
      if (requiredFailure) throw requiredFailure.reason;
      setAccounts(results[0].value.data.accounts || []);
      setDemands(results[1].value.data.demands || []);
      setReceipts(results[2].value.data.receipts || []);
      setExemptions(results[3].status === 'fulfilled' ? results[3].value.data.exemptions || [] : []);
      setAppeals(results[4].status === 'fulfilled' ? results[4].value.data.appeals || [] : []);
      setClaims(results[5].status === 'fulfilled' ? results[5].value.data.claims || [] : []);
      setIssues(results[6].status === 'fulfilled' ? results[6].value.data.issues || [] : []);
      setOnlinePayment(results[7].status === 'fulfilled'
        ? { ...results[7].value.data.online_payment, loading: false }
        : { available: false, loading: false, message: 'सध्या ऑनलाइन कर भरणा उपलब्ध नाही. कृपया कर भरण्यासाठी ग्रामपंचायत कार्यालयाला भेट द्या.' });
      setRegistryRequests(results[8].status === 'fulfilled' ? results[8].value.data.requests || [] : []);
      setSelected(current => current.filter(uid => (results[1].value.data.demands || []).some(d => d.demand_uid === uid && isPayableDemand(d))));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, user]);

  useEffect(() => { refresh(); }, [refresh]);

  const selectedDemands = useMemo(() => selectedPayableDemands(demands, selected), [demands, selected]);
  const payable = useMemo(() => demands.filter(isPayableDemand), [demands]);
  const demandAccountGroups = useMemo(() => groupDemandsByAccount(demands), [demands]);
  const demandSubjectGroups = useMemo(() => demandAccountGroups
    .map(group => ({
      ...group,
      subject_type: String(group.subject_key || '').split(':')[0],
      year_groups: groupDemandsByFinancialYear(group.demands),
      summary: summarizeDemands(group.demands),
    }))
    .sort((left, right) => {
      const order = { property: 1, business: 2, service_connection: 3 };
      const typeDifference = (order[left.subject_type] || 9) - (order[right.subject_type] || 9);
      return typeDifference || String(left.subject_name || '').localeCompare(String(right.subject_name || ''), 'mr');
    }), [demandAccountGroups]);
  const subjectAccounts = useMemo(() => groupTaxAccountsBySubject(accounts, demands), [accounts, demands]);
  const linkedSubjectText = useMemo(() => {
    const counts = subjectAccounts.reduce((summary, account) => {
      summary[account.subject_type] = (summary[account.subject_type] || 0) + 1;
      return summary;
    }, {});
    const number = value => Number(value || 0).toLocaleString('mr-IN');
    const parts = [
      counts.property ? `${number(counts.property)} करपात्र मालमत्ता` : null,
      counts.business ? `${number(counts.business)} व्यवसाय` : null,
      counts.service_connection ? `${number(counts.service_connection)} सेवा जोडण्या` : null,
    ].filter(Boolean);
    const linkedItems = parts.length > 1
      ? `${parts.slice(0, -1).join(', ')} आणि ${parts.at(-1)}`
      : parts[0];
    return parts.length
      ? `तुमच्या खात्याशी ${linkedItems} जोडलेल्या आहेत. खाली त्यांची एकत्रित करस्थिती दिली आहे.`
      : 'तुमच्या खात्याशी जोडलेल्या करपात्र नोंदींचा एकत्रित सारांश.';
  }, [subjectAccounts]);
  const selectedSummary = useMemo(() => summarizeDemands(selectedDemands), [selectedDemands]);
  const overallSummary = useMemo(() => summarizeDemands(demands), [demands]);
  const selectedTotal = selectedSummary.payable_balance;
  const totalOutstanding = overallSummary.outstanding_balance;

  const toggleDemand = uid => setSelected(current => current.includes(uid)
    ? current.filter(item => item !== uid)
    : [...current, uid]);

  const selectSubjectDemands = group => {
    const payableUids = group.demands.filter(isPayableDemand).map(demand => demand.demand_uid);
    setSelected(current => [...new Set([...current, ...payableUids])]);
  };

  const startPayment = async () => {
    if (!selectedDemands.length || !onlinePayment.available) return;
    setBusy(true); setError(''); setMessage('');
    const key = makeIdempotencyKey('tax-online');
    try {
      const response = await taxEngineAPI.createPaymentOrder(
        tenantSlug,
        { allocations: paymentAllocations(selectedDemands) },
        key
      );
      const intent = response.data.payment_intent;
      if (intent.status === 'requires_reconciliation' || !intent.provider_order_id) {
        setMessage(`भरणा विनंती ${intent.intent_uid} कार्यालयाकडून तपासली जात आहे. पुन्हा भरणा करू नका.`);
        setConfirmPay(false);
        await refresh();
        return;
      }
      if (!(await loadRazorpay())) throw new Error('सुरक्षित भरणा खिडकी उघडता आली नाही. कोणतीही रक्कम वजा झालेली नाही.');
      await new Promise((resolve, reject) => {
        const checkout = new window.Razorpay({
          key: intent.key_id,
          order_id: intent.provider_order_id,
          amount: intent.amount_subunits,
          currency: intent.currency,
          name: 'ग्रामपंचायत कर भरणा',
          description: `${intent.demands.length} कर मागण्यांचा भरणा`,
          prefill: { name: user?.full_name, email: user?.email, contact: user?.phone },
          handler: async result => {
            try {
              const verified = await taxEngineAPI.verifyPayment(tenantSlug, {
                intent_uid: intent.intent_uid,
                razorpay_order_id: result.razorpay_order_id,
                razorpay_payment_id: result.razorpay_payment_id,
                razorpay_signature: result.razorpay_signature,
              });
              if (verified.data.reconciliation_required) {
                setMessage('भरणा प्राप्त झाला आहे आणि कार्यालयीन ताळमेळ प्रलंबित आहे. पुन्हा भरणा करू नका.');
              } else {
                setMessage(`भरणा यशस्वी झाला. पावती ${verified.data.receipt?.receipt_number || ''}`);
              }
              resolve();
            } catch (err) { reject(err); }
          },
          modal: { ondismiss: () => reject(new Error('भरणा खिडकी बंद झाली. कोणताही नवीन भरणा निश्चित झालेला नाही.')) },
          theme: { color: '#075985' },
        });
        checkout.open();
      });
      setSelected([]); setConfirmPay(false); await refresh(); setTab('receipts');
    } catch (err) {
      if (['ONLINE_PAYMENT_NOT_CONFIGURED', 'WEBHOOK_NOT_CONFIGURED'].includes(err?.response?.data?.code)
        || /online payment|razorpay webhook/i.test(apiErrorMessage(err))) {
        const unavailable = 'सध्या ऑनलाइन कर भरणा उपलब्ध नाही. कृपया कर भरण्यासाठी ग्रामपंचायत कार्यालयाला भेट द्या.';
        setOnlinePayment({ available: false, loading: false, message: unavailable });
        setConfirmPay(false);
        setError(unavailable);
      } else {
        setError(apiErrorMessage(err));
      }
    } finally { setBusy(false); }
  };

  const downloadReceipt = async receipt => {
    const downloadKey = `receipt:${receipt.receipt_uid}`;
    setDocumentDownloadKey(downloadKey);
    try {
      const response = await taxEngineAPI.downloadMyReceipt(tenantSlug, receipt.receipt_uid);
      saveBlob(response.data, `${receipt.receipt_number || 'tax-receipt'}.pdf`);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setDocumentDownloadKey('');
    }
  };

  const viewReceipt = async receipt => {
    setReceiptPreviewLoading(receipt.receipt_uid);
    setError('');
    try {
      const response = await taxEngineAPI.downloadMyReceipt(tenantSlug, receipt.receipt_uid);
      setReceiptPreview({
        blob: response.data,
        fileName: `${receipt.receipt_number || 'tax-receipt'}.pdf`,
        title: `भरणा पावती ${receipt.receipt_number || ''}`.trim(),
      });
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setReceiptPreviewLoading('');
    }
  };

  const downloadConsolidatedNotice = async group => {
    const downloadKey = `notice:${group.account_uid}`;
    setDocumentDownloadKey(downloadKey);
    try {
      const response = await taxEngineAPI.downloadMyAccountDemandNotice(tenantSlug, group.account_uid);
      saveBlob(response.data, `${group.account_number || 'tax-account'}-consolidated-demand-notice.pdf`);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setDocumentDownloadKey('');
    }
  };

  const viewConsolidatedNotice = async group => {
    setDemandNoticePreviewLoading(group.account_uid);
    setError('');
    try {
      const response = await taxEngineAPI.downloadMyAccountDemandNotice(tenantSlug, group.account_uid);
      setDemandNoticePreview({
        blob: response.data,
        fileName: `${group.account_number || 'tax-account'}-consolidated-demand-notice.pdf`,
        title: `${group.subject_name} यांचे एकत्रित मागणी बिल`,
      });
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setDemandNoticePreviewLoading('');
    }
  };

  const submitExemption = async event => {
    event.preventDefault(); setBusy(true); setError('');
    const form = new FormData(event.currentTarget);
    try {
      const percentage = form.get('percentage');
      const fixedAmount = form.get('fixed_amount');
      if (Boolean(percentage) === Boolean(fixedAmount)) throw new Error('टक्केवारी किंवा निश्चित सवलत रक्कम यापैकी एकच भरा.');
      await taxEngineAPI.requestExemption(tenantSlug, exemptionAccount.account_uid, {
        exemption_type: form.get('exemption_type'),
        percentage: percentage || undefined,
        fixed_amount: percentage ? undefined : fixedAmount,
        valid_from: form.get('valid_from'), valid_to: form.get('valid_to') || null,
        reason: form.get('reason'), evidence: { note: form.get('evidence_note') || null },
      });
      setExemptionAccount(null); setMessage('सवलतीची विनंती तपासणीसाठी सादर केली.'); await refresh();
    } catch (err) { setError(apiErrorMessage(err)); } finally { setBusy(false); }
  };

  const submitAppeal = async event => {
    event.preventDefault(); setBusy(true); setError('');
    const form = new FormData(event.currentTarget);
    try {
      await taxEngineAPI.createAppeal(tenantSlug, appealDemand.demand_uid, {
        reason_code: form.get('reason_code'), statement: form.get('statement'),
        stay_requested: form.get('stay_requested') === 'on', evidence: { note: form.get('evidence_note') || null },
      });
      setAppealDemand(null); setMessage('अपील सादर झाले. तपासणी पूर्ण होईपर्यंत वसुली स्थगित राहील.'); await refresh();
    } catch (err) { setError(apiErrorMessage(err)); } finally { setBusy(false); }
  };

  const submitClaim = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      await identityAPI.createClaim(tenantSlug, {
        target_type: claimForm.target_type, target_uid: claimForm.target_uid,
        requested_relationship: claimForm.requested_relationship,
        requested_scopes: claimForm.target_type === 'business'
          ? ['view', 'pay', 'view_receipts', 'download_receipts', 'submit_tax_declaration']
          : ['view', 'pay', 'view_receipts', 'download_receipts'],
        evidence: { note: claimForm.note, attestation: true },
      });
      setClaimForm({ target_type: 'property', target_uid: '', requested_relationship: 'owner', note: '' });
      setClaimSearch({ query: '', results: [], loading: false, searched: false });
      setMessage('नोंद प्रवेशाची विनंती ग्रामपंचायत पडताळणीसाठी सादर केली.'); await refresh();
    } catch (err) { setError(apiErrorMessage(err)); } finally { setBusy(false); }
  };

  const searchClaimRecords = async () => {
    const query = claimSearch.query.trim();
    if (query.length < 3) {
      setError('शोधण्यासाठी किमान ३ अक्षरे किंवा क्रमांक भरा.');
      return;
    }
    setClaimSearch(current => ({ ...current, loading: true, searched: true, results: [] }));
    setError('');
    try {
      const response = await identityAPI.searchClaimTargets(tenantSlug, {
        target_type: claimForm.target_type,
        q: query,
      });
      setClaimSearch(current => ({ ...current, loading: false, results: response.data.targets || [] }));
    } catch (err) {
      setClaimSearch(current => ({ ...current, loading: false, results: [] }));
      setError(apiErrorMessage(err));
    }
  };

  const withdrawClaim = async claim => {
    const reason = window.prompt('ही जोडणी चुकीची का आहे? निश्चित केल्यानंतर प्रवेश रद्द केला जाईल.');
    if (!reason) return;
    try { await identityAPI.withdrawClaim(tenantSlug, claim.claim_uid, { reason }); await refresh(); }
    catch (err) { setError(apiErrorMessage(err)); }
  };

  const submitIssue = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      await identityAPI.createIssue(tenantSlug, {
        ...issueForm,
        evidence: { note: issueForm.statement, attestation: true },
      });
      setIssueForm({ target_type: 'tax_account', target_uid: '', issue_type: 'incorrect_link', statement: '' });
      setMessage('जोडणीची समस्या नोंदवली. कार्यालयाचा निर्णय येथे पाहता येईल.'); await refresh();
    } catch (err) { setError(apiErrorMessage(err)); } finally { setBusy(false); }
  };

  const submitRegistryRequest = async event => {
    event.preventDefault(); setBusy(true); setError('');
    try {
      await identityAPI.createRegistryRequest(tenantSlug, {
        request_type: registryForm.request_type,
        reference_number: registryForm.reference_number || undefined,
        statement: registryForm.statement,
        evidence: { note: registryForm.statement, attestation: true },
      });
      setRegistryForm({ request_type: 'new_property', reference_number: '', statement: '' });
      setMessage('नोंदणी सहाय्य अर्ज ग्रामपंचायत तपासणीसाठी सादर केला. या अर्जामुळे अधिकृत नोंद आपोआप बदलत नाही.');
      await refresh();
    } catch (err) { setError(apiErrorMessage(err)); } finally { setBusy(false); }
  };

  if (!user) return <section className="tax-workspace-login"><h1>माझे कर खाते</h1><p>आपल्या खात्याशी पडताळून जोडलेल्या मालमत्ता आणि व्यवसायांचे कर पाहण्यासाठी लॉग इन करा.</p><Link className="tax-primary" to={`/login?gp=${tenantSlug}`}>लॉग इन करा</Link></section>;

  return <section className="tax-workspace">
    <header className="tax-hero">
      <div className="tax-hero-content">
        <div className="tax-hero-heading"><span className="tax-eyebrow">सुरक्षित करदाता सेवा</span><h1>माझे कर खाते</h1><p>{linkedSubjectText}</p></div>
        <div className="tax-hero-total"><span>दंडासह एकूण कर बाकी</span><strong>{money(totalOutstanding)}</strong><small>{overallSummary.open_demand_count} बाकी मागण्या · {overallSummary.payable_demand_count} आता देय</small><div className="tax-amount-breakdown"><span>मूळ कर बाकी <b>{money(overallSummary.outstanding_principal)}</b></span><span className="tax-penalty-text">नियम २०(३) दंड बाकी <b>{money(overallSummary.outstanding_penalty)}</b></span><span>आता भरावयाची रक्कम <b>{money(overallSummary.payable_balance)}</b></span>{overallSummary.appeal_hold_balance > 0 && <span>अपीलमुळे स्थगित <b>{money(overallSummary.appeal_hold_balance)}</b></span>}</div></div>
      </div>
    </header>
    <TaxStatutoryDisclosure />
    {/* <div className="tax-notice">
      <span>पूर्वीच्या घरपट्टी नोंदींसह मालमत्ता, पाणी, सेवा आणि व्यवसाय कर याच सुरक्षित खात्यात उपलब्ध आहेत.</span>
    </div> */}
    <Notice type="error">{error}</Notice><Notice type="success">{message}</Notice>
    <nav className="tax-tabs" aria-label="कर खात्याचे विभाग">
      {[['accounts', 'कर खाती'], ['demands', 'मागण्या व भरणा'], ['receipts', 'पावत्या'], ['cases', 'विनंत्या व अपील'], ['access', 'नोंद मदत']].map(([key, label]) =>
        <button key={key} className={tab === key ? 'active' : ''} onClick={() => { setConfirmPay(false); setTab(key); }}>{label}</button>)}
    </nav>
    {loading ? <LoadingSpinner fullPage message="पडताळलेल्या कर नोंदी लोड होत आहेत…" /> : null}

    {!loading && tab === 'accounts' && <div className="tax-card-grid">
      {!accounts.length && <div className="tax-empty"><h2>जोडलेले कर खाते नाही</h2><p>देय मागणी नसली तरी आपले वापरकर्ता खाते वैध असू शकते. खाते जोडणी विभागातून हरवलेल्या खात्याची विनंती करा.</p><button className="tax-primary" onClick={() => setTab('access')}>हरवलेले खाते कळवा</button></div>}
      {subjectAccounts.map(account => <article className="tax-account-card" key={account.subject_key}>
        <div className="tax-card-head"><span className={`tax-status ${account.status}`}>{statusLabel(account.status)}</span><span>{subjectTypeLabel(account.subject_type)}</span></div>
        <h2>{subjectLabel(account)}</h2><code>{account.subject?.secondary_name || account.account_number}</code>
        <dl><div><dt>कर नोंद</dt><dd>एकत्रित कर खाते{account.ledger_accounts.length > 1 ? ` · ${account.ledger_accounts.length} अंतर्गत लेखे` : ''}</dd></div><div><dt>बाकी मागण्या</dt><dd>{account.demand_summary?.open_demand_count || 0}</dd></div><div><dt>मूळ कर बाकी</dt><dd>{money(account.demand_summary?.outstanding_principal)}</dd></div><div className="tax-penalty-cell"><dt>नियम २०(३) दंड बाकी</dt><dd>{money(account.demand_summary?.outstanding_penalty)}</dd></div><div className="tax-total-cell"><dt>दंडासह एकूण बाकी</dt><dd>{money(account.demand_summary?.outstanding_balance)}</dd></div><div><dt>आता भरावयाची</dt><dd>{money(account.demand_summary?.payable_balance)}</dd></div>{Number(account.demand_summary?.appeal_hold_balance) > 0 && <div><dt>अपीलमुळे स्थगित</dt><dd>{money(account.demand_summary?.appeal_hold_balance)}</dd></div>}<div><dt>थकीत</dt><dd>{money(account.demand_summary?.overdue_balance)}</dd></div></dl>
        <GoogleLocationMap latitude={account.subject?.latitude} longitude={account.subject?.longitude} title={account.subject?.name || account.account_number} address={account.subject?.address} compact />
        <div className="tax-actions"><button onClick={() => { setTab('demands'); }}>मागण्या पाहा</button>{account.granted_scopes?.includes('submit_documents') && <button onClick={() => setExemptionAccount(account)}>सवलतीची विनंती</button>}<button onClick={() => { setIssueForm({ target_type:'tax_account', target_uid:account.account_uid, issue_type:'incorrect_link', statement:'' }); setTab('access'); }}>चुकीची जोडणी कळवा</button></div>
      </article>)}
    </div>}

    {!loading && tab === 'demands' && <>
      {confirmPay ? (
        <section className="tax-confirm-screen" aria-labelledby="tax-confirm-heading">
          <div className="tax-confirm-header">
            <div className="tax-confirm-top-bar">
              <button
                type="button"
                className="tax-confirm-back-link"
                disabled={busy}
                onClick={() => setConfirmPay(false)}
              >
                ← मागण्या निवडीकडे परत जा (मागण्या बदला)
              </button>
              <span className="tax-confirm-step-pill">पायरी २: भरणा पडताळणी</span>
            </div>
            <div className="tax-confirm-title-row">
              <div>
                <h2 id="tax-confirm-heading">भरणा निश्चित करा</h2>
                <p>खालील निवडलेल्या {selectedDemands.length} मागण्यांचा भरणा सुरक्षित पेमेंट गेटवेद्वारे केला जाईल. रक्कम व तपशील तपासून भरणा करा.</p>
              </div>
              <div className="tax-confirm-total-badge">
                <span>एकूण देय भरणा</span>
                <strong>{money(selectedTotal)}</strong>
              </div>
            </div>
          </div>

          <div className="tax-confirm-card">
            <div className="tax-confirm-card-head">
              <h3>निवडलेल्या मागण्यांचा तपशील</h3>
              <span>{selectedDemands.length} मागण्या समाविष्ट</span>
            </div>
            <ul className="tax-confirm-list">
              {selectedDemands.map(demand => {
                const breakdown = demandAmountBreakdown(demand);
                const subjectRef = demandSubjectReference(demand) || demand.display?.subject_type_name;
                return (
                  <li key={demand.demand_uid} className="tax-confirm-item">
                    <div className="tax-confirm-item-main">
                      <div className="tax-confirm-item-title">
                        <strong>{demandTaxName(demand)}</strong>
                        {demand.financial_year && (
                          <span className="tax-confirm-year-pill">आर्थिक वर्ष {demand.financial_year}</span>
                        )}
                        <span className={`tax-status ${demand.status}`}>{statusLabel(demand.status)}</span>
                      </div>
                      <div className="tax-confirm-item-subject">
                        <span className="tax-subject-icon" aria-hidden="true">🏠</span>
                        <span className="tax-subject-name">{demandSubjectName(demand)}</span>
                        {subjectRef && <span className="tax-subject-ref">({subjectRef})</span>}
                      </div>
                      <div className="tax-confirm-item-meta">
                        <span className="tax-meta-item">
                          मागणी क्र. <code className="tax-code-chip">{demand.demand_number}</code>
                        </span>
                        <span className="tax-meta-bullet">•</span>
                        <span className="tax-meta-item">
                          मूळ कर: <b>{money(breakdown.outstandingPrincipal)}</b>
                        </span>
                        {Number(breakdown.outstandingPenalty) > 0 && (
                          <>
                            <span className="tax-meta-bullet">•</span>
                            <span className="tax-meta-item tax-penalty-text">
                              नियम २०(३) दंड: <b>{money(breakdown.outstandingPenalty)}</b>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="tax-confirm-item-amount">
                      <span className="tax-confirm-amount-label">देय रक्कम</span>
                      <strong className="tax-confirm-amount-value">{money(demandBalance(demand))}</strong>
                    </div>
                  </li>
                );
              })}
            </ul>

            <div className="tax-confirm-summary-panel">
              <div className="tax-confirm-summary-row">
                <span>निवडलेल्या मागण्यांची संख्या:</span>
                <strong>{selectedDemands.length} मागण्या</strong>
              </div>
              <div className="tax-confirm-summary-row">
                <span>एकूण मूळ कर (Principal):</span>
                <span>{money(selectedSummary.outstanding_principal)}</span>
              </div>
              {Number(selectedSummary.outstanding_penalty) > 0 && (
                <div className="tax-confirm-summary-row tax-penalty-row">
                  <span>नियम २०(३) दंड (Penalty):</span>
                  <span className="tax-penalty-text">{money(selectedSummary.outstanding_penalty)}</span>
                </div>
              )}
              <div className="tax-confirm-grand-total-row">
                <div>
                  <strong>दंडासह एकूण भरणा</strong>
                  <small>शासकीय नियमांनुसार थेट ग्रामपंचायत अधिकृत खात्यात वर्ग होईल</small>
                </div>
                <strong className="tax-confirm-grand-total">{money(selectedTotal)}</strong>
              </div>
            </div>
          </div>

          <Notice>सुरक्षित भरणा खिडकी उघडल्यानंतर पृष्ठ पुन्हा लोड करू नका किंवा याच मागण्यांसाठी दुसरा भरणा सुरू करू नका.</Notice>

          <div className="tax-confirm-footer">
            <div className="tax-confirm-trust-info">
              <span className="tax-trust-icon" aria-hidden="true">🔒</span>
              <div>
                <strong>सुरक्षित ऑनलाइन भरणा</strong>
                <small>256-bit SSL एनक्रिप्शन व Razorpay द्वारे अधिकृत भरणा</small>
              </div>
            </div>
            <div className="tax-confirm-actions">
              <button
                type="button"
                className="tax-confirm-secondary-btn"
                disabled={busy}
                onClick={() => setConfirmPay(false)}
              >
                मागे जा / मागण्या बदला
              </button>
              <button
                type="button"
                className="tax-primary tax-confirm-pay-btn"
                disabled={busy || !selectedDemands.length || !onlinePayment.available}
                onClick={startPayment}
              >
                {busy ? (
                  <>
                    <TaxDocumentSpinner /> सुरक्षित भरणा उघडत आहे…
                  </>
                ) : (
                  <>सुरक्षित भरणा करा · {money(selectedTotal)}</>
                )}
              </button>
            </div>
          </div>
        </section>
      ) : (
        <>
          <div className="tax-toolbar"><div><strong>कोणतीही मागणी आपोआप निवडली जात नाही.</strong><span> आपण भरू इच्छित असलेली प्रत्येक मागणी निवडा.</span></div><button disabled={!payable.length} onClick={() => setSelected(payable.map(item => item.demand_uid))}>सर्व बाकी भरा</button></div>
          <div className="tax-table-wrap"><table className="tax-table"><thead><tr><th scope="col">निवडा</th><th>कराचा तपशील</th><th>मालमत्ता किंवा व्यवसाय</th><th>कालावधी व देय दिनांक</th><th>स्थिती</th><th className="number">आकारणी व दंड</th><th className="number">भरले</th><th className="number">दंडासह बाकी</th><th>कृती</th></tr></thead><tbody>
            {!demands.length && <tr><td colSpan="9" className="tax-empty-cell">या खात्याशी कोणतीही जारी मागणी जोडलेली नाही.</td></tr>}
            {demandSubjectGroups.map(group => <React.Fragment key={group.subject_key}>
              <tr className={`tax-subject-row tax-subject-${group.subject_type}`}>
                <th colSpan="9">
                  <div className="tax-subject-heading">
                    <div className="tax-subject-identity">
                      <span className="tax-subject-badge">{subjectTypeLabel(group.subject_type)}</span>
                      <span>
                        <strong>{group.subject_name}</strong>
                        <small>{group.subject_reference || group.account_number}</small>
                      </span>
                    </div>
                    <div className="tax-subject-summary">
                      <span>{group.summary.open_demand_count} बाकी मागण्या</span>
                      <span>भरले {money(group.summary.total_paid)}</span>
                      <strong>एकूण बाकी {money(group.summary.outstanding_balance)}</strong>
                      <button
                        type="button"
                        disabled={!group.demands.some(isPayableDemand)}
                        onClick={() => selectSubjectDemands(group)}
                      >
                        या नोंदीची बाकी निवडा
                      </button>
                    </div>
                  </div>
                </th>
              </tr>
              {group.year_groups.map(([financialYear, yearDemands]) => <React.Fragment key={`${group.subject_key}:${financialYear}`}>
                <tr className="tax-year-row">
                  <th colSpan="9">
                    <span>आर्थिक वर्ष {financialYear}</span>
                    <small>{yearDemands.length} कर मागण्या</small>
                    <strong>बाकी {money(yearDemands.reduce((total, demand) => total + demandBalance(demand), 0))}</strong>
                  </th>
                </tr>
                {yearDemands.map(demand => <tr key={demand.demand_uid}>
                  <td>{isPayableDemand(demand) ? <input aria-label={`${demandTaxName(demand)} निवडा`} type="checkbox" checked={selected.includes(demand.demand_uid)} onChange={() => toggleDemand(demand.demand_uid)} /> : '—'}</td>
                  <td><strong>{demandTaxName(demand)}</strong>{Number(demand.penalty_amount) > 0 && <small className="tax-review-warning">नियम २०(३) दंड {money(demand.penalty_amount)}</small>}{demand.display?.tax_name_needs_review && <small className="tax-review-warning">ग्रामपंचायत तपासणी प्रलंबित</small>}<small>मागणी क्र. {demand.demand_number}</small></td><td><strong>{demandSubjectName(demand)}</strong><small>{demandSubjectReference(demand) || demand.display?.subject_type_name}</small></td><td>{shortDate(demand.period_start)} – {shortDate(demand.period_end)}<small>देय दिनांक {shortDate(demand.due_date)}</small></td><td><span className={`tax-status ${demand.status}`}>{statusLabel(demand.status)}</span></td><td className="number"><strong>{money(demand.total_amount)}</strong><small>मूळ {money(demandAmountBreakdown(demand).principal)}</small><small className="tax-penalty-text">दंड {money(demandAmountBreakdown(demand).penalty)}</small></td><td className="number"><strong>{money(demand.paid_amount)}</strong><DemandPaymentDetails demand={demand} /></td><td className="number"><strong>{money(demandBalance(demand))}</strong><small>मूळ {money(demandAmountBreakdown(demand).outstandingPrincipal)}</small><small className="tax-penalty-text">दंड {money(demandAmountBreakdown(demand).outstandingPenalty)}</small></td><td>{['issued', 'partially_paid', 'overdue'].includes(demand.status) && <button onClick={() => setAppealDemand(demand)}>अपील करा</button>}</td>
                </tr>)}
              </React.Fragment>)}
            </React.Fragment>)}
          </tbody></table></div>
          {!onlinePayment.loading && !onlinePayment.available && <Notice type="warning">{onlinePayment.message}</Notice>}
          <div className="tax-pay-bar"><span>{selectedDemands.length} मागण्या निवडल्या<small>मूळ {money(selectedSummary.outstanding_principal)} · नियम २०(३) दंड {money(selectedSummary.outstanding_penalty)}</small></span><strong>{money(selectedTotal)}</strong><button className="tax-primary" disabled={!selectedDemands.length || busy || onlinePayment.loading || !onlinePayment.available} onClick={() => { setConfirmPay(true); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>{onlinePayment.loading ? 'ऑनलाइन भरणा तपासत आहे…' : onlinePayment.available ? 'भरणा तपासा' : 'ऑनलाइन भरणा उपलब्ध नाही'}</button></div>
        </>
      )}
    </>}

    {!loading && tab === 'receipts' && <div className="tax-documents">
      <header className="tax-document-section-head">
        <div className="tax-document-heading-icon" aria-hidden="true">▤</div>
        <div>
          <h2>मागणी बिले</h2>
          <p>सर्व आर्थिक वर्षांतील आकारणी, भरलेली रक्कम आणि बाकी एका अधिकृत बिलात.</p>
        </div>
      </header>
      {!demands.length && <div className="tax-empty"><p>मागणी बिल उपलब्ध नाही.</p></div>}
      {!!demandAccountGroups.length && <div className="tax-document-table-wrap">
        <table className="tax-document-table demand">
          <thead><tr><th>मालमत्ता / व्यवसाय</th><th>आर्थिक वर्षे</th><th>मागण्या</th><th>आकारणी</th><th>भरले</th><th>दंडासह बाकी</th><th>कृती</th></tr></thead>
          <tbody>{demandAccountGroups.map(group => {
            const subjectType = group.demands?.[0]?.account?.subject_type;
            const reference = readableDocumentReference(group.subject_reference);
            const viewLoading = demandNoticePreviewLoading === group.account_uid;
            const downloadLoading = documentDownloadKey === `notice:${group.account_uid}`;
            return <tr key={`notice-${group.account_uid}`}>
              <td className="identity" data-label="मालमत्ता / व्यवसाय"><span className={`tax-document-kind ${subjectType || ''}`}>{subjectTypeLabel(subjectType)}</span><strong>{group.subject_name}</strong>{reference && <small>{reference}</small>}</td>
              <td data-label="आर्थिक वर्षे"><strong>{group.financial_years.join(', ')}</strong></td>
              <td data-label="मागण्या"><strong>{group.demands.length}</strong><small>कर मागण्या</small></td>
              <td data-label="आकारणी"><strong>{money(group.total_amount)}</strong><small>मूळ {money(group.principal_amount)} · दंड {money(group.penalty_amount)}</small></td>
              <td data-label="भरले"><strong className="paid">{money(group.paid_amount)}</strong></td>
              <td data-label="दंडासह बाकी"><strong className="balance">{money(group.balance_amount)}</strong><small>त्यातील दंड {money(group.outstanding_penalty)}</small></td>
              <td className="actions" data-label="कृती"><div className="tax-document-actions">
                <button type="button" disabled={viewLoading} title={viewLoading ? 'बिल उघडत आहे…' : 'एकत्रित मागणी बिल पहा'} aria-label={`${group.subject_name} यांचे एकत्रित मागणी बिल पहा`} onClick={() => viewConsolidatedNotice(group)}>{viewLoading ? <TaxDocumentSpinner /> : <TaxDocumentIcon name="view" />}</button>
                <button className="download" type="button" disabled={downloadLoading} title={downloadLoading ? 'PDF डाउनलोड होत आहे…' : 'PDF डाउनलोड करा'} aria-label={`${group.subject_name} यांचे मागणी बिल PDF डाउनलोड करा`} onClick={() => downloadConsolidatedNotice(group)}>{downloadLoading ? <TaxDocumentSpinner /> : <TaxDocumentIcon name="download" />}</button>
              </div></td>
            </tr>;
          })}</tbody>
        </table>
      </div>}

      <header className="tax-document-section-head receipts">
        <div className="tax-document-heading-icon" aria-hidden="true">✓</div>
        <div>
          <h2>भरणा पावत्या</h2>
          <p>फक्त पडताळणी पूर्ण झालेल्या भरण्यांच्या अधिकृत पावत्या.</p>
        </div>
      </header>
      {!receipts.length && <div className="tax-empty"><h2>अद्याप भरणा पावती नाही</h2><p>यशस्वी ऑनलाइन भरणे आणि कार्यालयात ताळमेळ झालेल्या वसुलीच्या पावत्या येथे दिसतील.</p></div>}
      {!!receipts.length && <div className="tax-document-table-wrap">
        <table className="tax-document-table receipt">
          <thead><tr><th>पावती</th><th>भरणा दिनांक</th><th>भरणा पद्धत</th><th>मागण्या</th><th>भरलेली रक्कम</th><th>कृती</th></tr></thead>
          <tbody>{receipts.map(receipt => {
            const viewLoading = receiptPreviewLoading === receipt.receipt_uid;
            const downloadLoading = documentDownloadKey === `receipt:${receipt.receipt_uid}`;
            return <tr key={receipt.receipt_uid}>
              <td className="identity" data-label="पावती"><span className={`tax-status ${receipt.status}`}>{statusLabel(receipt.status)}</span><strong>{receipt.receipt_number}</strong><small>{receiptAllocationSummary(receipt)}</small></td>
              <td data-label="भरणा दिनांक"><strong>{shortDate(receipt.paid_at)}</strong></td>
              <td data-label="भरणा पद्धत"><strong>{paymentModeLabel(receipt.payment_mode)}</strong>{receipt.provider === 'razorpay' && receipt.provider_payment_id && <small>व्यवहार क्र. <code>{receipt.provider_payment_id}</code></small>}</td>
              <td data-label="मागण्या"><strong>{receipt.allocations?.length || 0}</strong><small>समाविष्ट मागण्या</small></td>
              <td data-label="भरलेली रक्कम"><strong className="paid">{money(receipt.amount)}</strong></td>
              <td className="actions" data-label="कृती"><div className="tax-document-actions">
                <button type="button" disabled={viewLoading} title={viewLoading ? 'पावती उघडत आहे…' : 'पावती पहा'} aria-label={`${receipt.receipt_number} पावती पहा`} onClick={() => viewReceipt(receipt)}>{viewLoading ? <TaxDocumentSpinner /> : <TaxDocumentIcon name="view" />}</button>
                <button className="download" type="button" disabled={downloadLoading} title={downloadLoading ? 'PDF डाउनलोड होत आहे…' : 'PDF डाउनलोड करा'} aria-label={`${receipt.receipt_number} पावती PDF डाउनलोड करा`} onClick={() => downloadReceipt(receipt)}>{downloadLoading ? <TaxDocumentSpinner /> : <TaxDocumentIcon name="download" />}</button>
              </div></td>
            </tr>;
          })}</tbody>
        </table>
      </div>}
    </div>}

    {!loading && tab === 'cases' && <div className="tax-case-columns"><section><h2>कर सवलती</h2>{exemptions.map(item => <article className="tax-list-card" key={item.exemption_uid}><div><span className={`tax-status ${item.status}`}>{statusLabel(item.status)}</span><strong>सवलत विनंती</strong><p>{item.percentage ? `${item.percentage}%` : money(item.fixed_amount)} · {shortDate(item.valid_from)} ते {shortDate(item.valid_to)}</p><small>{item.reason}</small></div>{item.status === 'pending' && <button onClick={async () => { await taxEngineAPI.withdrawMyExemption(tenantSlug, item.exemption_uid, { version: item.version, reason: 'करदात्याने विनंती मागे घेतली' }); refresh(); }}>मागे घ्या</button>}</article>)}{!exemptions.length && <p>सवलतीची विनंती नाही.</p>}</section>
      <section><h2>अपील</h2>{appeals.map(item => <article className="tax-list-card" key={item.appeal_uid}><div><span className={`tax-status ${item.status}`}>{statusLabel(item.status)}</span><strong>मागणी क्र. {item.demand?.demand_number}</strong><p>{item.statement}</p>{item.hearing_at && <small>सुनावणी {shortDate(item.hearing_at)}</small>}</div>{['submitted', 'under_review', 'hearing'].includes(item.status) && <button onClick={async () => { const reason = window.prompt('अपील मागे घेण्याचे कारण'); if (reason) { await taxEngineAPI.withdrawMyAppeal(tenantSlug, item.appeal_uid, { version: item.version, reason }); refresh(); } }}>मागे घ्या</button>}</article>)}{!appeals.length && <p>कोणतेही अपील नाही.</p>}</section></div>}

    {!loading && tab === 'access' && <div className="tax-access-shell">
      <div className="tax-access-guidance">
        <strong>येथून मालमत्ता, व्यवसाय किंवा कर आकारणी तयार होत नाही.</strong>
        <span>अधिकृत नोंदी ग्रामपंचायत अधिकारीच तयार आणि बदलतात. येथे आपण विद्यमान नोंद आपल्या loginला जोडण्याची विनंती किंवा नवीन/दुरुस्ती अर्ज सादर करू शकता.</span>
      </div>
      <div className="tax-access-grid">
        <section className="tax-access-primary"><span className="tax-step">१</span><h2>विद्यमान नोंद माझ्या खात्यात दाखवा</h2><p>मिळकत क्रमांक, assessment क्रमांक, व्यवसाय नोंदणी क्रमांक, कर खाते किंवा मागणी क्रमांक वापरून GP register मधील नोंद शोधा.</p>
          <form className="tax-form" onSubmit={submitClaim}>
            <label>नोंद प्रकार<select value={claimForm.target_type} onChange={e => { const target_type=e.target.value; setClaimForm({ target_type, target_uid:'', requested_relationship:CLAIM_RELATIONSHIPS[target_type][0], note:'' }); setClaimSearch({ query:'', results:[], loading:false, searched:false }); }}><option value="property">मालमत्ता</option><option value="business">व्यवसाय</option><option value="tax_account">कर खाते किंवा मागणी</option></select></label>
            <label>नोंद शोधा<div className="tax-record-search"><input value={claimSearch.query} onChange={e => setClaimSearch({ ...claimSearch, query:e.target.value })} onKeyDown={e => { if(e.key==='Enter'){e.preventDefault();searchClaimRecords();} }} placeholder={claimForm.target_type==='property'?'मिळकत/Assessment/मालमत्ता क्रमांक':claimForm.target_type==='business'?'व्यवसाय नाव किंवा नोंदणी क्रमांक':'कर खाते किंवा मागणी क्रमांक'} autoComplete="off"/><button type="button" onClick={searchClaimRecords} disabled={claimSearch.loading}>{claimSearch.loading?'शोधत आहे…':'शोधा'}</button></div></label>
            {claimSearch.searched && <div className="tax-record-results" role="radiogroup" aria-label="जुळणाऱ्या नोंदी">{claimSearch.results.map(item=><label key={item.target_uid} className={claimForm.target_uid===item.target_uid?'selected':''}><input type="radio" name="claim_target" checked={claimForm.target_uid===item.target_uid} onChange={()=>setClaimForm({...claimForm,target_uid:item.target_uid})}/><span><strong>{item.title}</strong><small>{item.reference || 'नोंदणी संदर्भ उपलब्ध नाही'}{item.context ? ` · ${item.context}` : ''}</small></span></label>)}{!claimSearch.loading&&!claimSearch.results.length&&<div className="tax-record-empty">जुळणारी verified नोंद सापडली नाही. खालील “नवीन किंवा दुरुस्ती अर्ज” वापरा.</div>}</div>}
            {claimForm.target_uid && <><label>या नोंदीशी तुमचे नाते<select value={claimForm.requested_relationship} onChange={e=>setClaimForm({...claimForm,requested_relationship:e.target.value})}>{CLAIM_RELATIONSHIPS[claimForm.target_type].map(value=><option key={value} value={value}>{RELATIONSHIP_LABELS[value]||'इतर नाते'}</option>)}</select></label><label>पडताळणीसाठी माहिती<textarea required minLength="5" value={claimForm.note} onChange={e=>setClaimForm({...claimForm,note:e.target.value})} placeholder="उदा. मिळकत उताऱ्यावर माझे नाव आहे / मी अधिकृत प्रतिनिधी आहे"/></label><label className="tax-check"><input required type="checkbox"/> ही फक्त प्रवेश विनंती आहे; अधिकृत नोंद किंवा कर आकारणी मी स्वतः बदलत नाही, हे मला मान्य आहे.</label></>}
            <button className="tax-primary" disabled={busy||!claimForm.target_uid}>GP पडताळणीसाठी पाठवा</button>
          </form>
        </section>
        <section><span className="tax-step">२</span><h2>नवीन किंवा दुरुस्ती अर्ज</h2><p>नोंद सापडत नसल्यास, मालकी बदल, वारस नोंद, बांधकाम बदल किंवा व्यवसाय दुरुस्तीसाठी अर्ज करा. अधिकारी तपासणीनंतरच register बदलतील.</p>
          <form className="tax-form" onSubmit={submitRegistryRequest}><label>अर्ज प्रकार<select value={registryForm.request_type} onChange={e=>setRegistryForm({...registryForm,request_type:e.target.value})}>{Object.entries(REGISTRY_REQUEST_LABELS).map(([value,label])=><option key={value} value={value}>{label}</option>)}</select></label><label>उपलब्ध संदर्भ क्रमांक<input value={registryForm.reference_number} onChange={e=>setRegistryForm({...registryForm,reference_number:e.target.value})} placeholder="मिळकत, जुनी पावती, अर्ज किंवा व्यवसाय क्रमांक (असल्यास)"/></label><label>अर्जाचा संपूर्ण तपशील<textarea required minLength="15" value={registryForm.statement} onChange={e=>setRegistryForm({...registryForm,statement:e.target.value})} placeholder="मालमत्ता/व्यवसाय, मालक, पत्ता आणि अपेक्षित बदल स्पष्ट लिहा"/></label><label className="tax-check"><input required type="checkbox"/> माहिती खरी असून आवश्यक कागदपत्रे GP कार्यालयाला देण्यास मी तयार आहे.</label><button className="tax-primary" disabled={busy}>नोंदणी सहाय्य अर्ज पाठवा</button></form>
        </section>
        <section><h2>चुकीची जोडणी कळवा</h2><p>फक्त तुमच्या loginला आधीपासून दिसणाऱ्या चुकीच्या कर खात्यासाठी हा पर्याय वापरा.</p><form className="tax-form" onSubmit={submitIssue}><label>चुकीचे कर खाते<select required value={issueForm.target_uid} onChange={e=>setIssueForm({...issueForm,target_uid:e.target.value})}><option value="">कर खाते निवडा…</option>{accounts.map(account=><option key={account.account_uid} value={account.account_uid}>{account.subject?.name||account.account_number} · {account.account_number}</option>)}</select></label><label>समस्येचा प्रकार<select value={issueForm.issue_type} onChange={e=>setIssueForm({...issueForm,issue_type:e.target.value})}>{['incorrect_link','incorrect_relationship','duplicate_record','other'].map(value=><option key={value} value={value}>{ISSUE_TYPE_LABELS[value]}</option>)}</select></label><label>समस्येचा तपशील<textarea required minLength="10" value={issueForm.statement} onChange={e=>setIssueForm({...issueForm,statement:e.target.value})}/></label><button className="tax-primary" disabled={busy||!issueForm.target_uid}>समस्या सादर करा</button></form></section>
        <section className="tax-access-history"><h2>माझ्या विनंत्या आणि अर्ज</h2>
          {claims.map(claim => { const summary=claim.evidence?.record_summary; return <article className="tax-list-card" key={claim.claim_uid}><div><span className={`tax-status ${claim.status}`}>{statusLabel(claim.status)}</span><strong>नोंद प्रवेश विनंती · {summary?.title||subjectTypeLabel(claim.target_type)}</strong><p>{summary?.reference||RELATIONSHIP_LABELS[claim.requested_relationship]||'इतर नाते'}</p><small>{claim.review_note}</small></div>{['pending','approved'].includes(claim.status)&&<button onClick={()=>withdrawClaim(claim)}>{claim.status==='approved'?'मंजूर प्रवेश रद्द करा':'विनंती मागे घ्या'}</button>}</article>; })}
          {registryRequests.map(request=><article className="tax-list-card" key={request.request_uid}><div><span className={`tax-status ${request.status}`}>{statusLabel(request.status)}</span><strong>{REGISTRY_REQUEST_LABELS[request.request_type]||'नोंदणी सहाय्य अर्ज'}</strong><p>{request.reference_number||request.statement}</p>{request.resolution&&<small>कार्यालयीन निर्णय: {request.resolution}</small>}</div>{['submitted','under_review'].includes(request.status)&&<button onClick={async()=>{const reason=window.prompt('अर्ज मागे घेण्याचे कारण');if(reason){await identityAPI.withdrawRegistryRequest(tenantSlug,request.request_uid,{reason});refresh();}}}>अर्ज मागे घ्या</button>}</article>)}
          {issues.map(issue=><article className="tax-list-card" key={issue.issue_uid}><div><span className={`tax-status ${issue.status}`}>{statusLabel(issue.status)}</span><strong>{ISSUE_TYPE_LABELS[issue.issue_type]||'जोडणी समस्या'}</strong><p>{issue.statement}</p><small>{issue.resolution}</small></div>{issue.status==='submitted'&&<button onClick={async()=>{const reason=window.prompt('विनंती मागे घेण्याचे कारण');if(reason){await identityAPI.withdrawIssue(tenantSlug,issue.issue_uid,{version:issue.version,reason});refresh();}}}>मागे घ्या</button>}</article>)}
          {!claims.length&&!issues.length&&!registryRequests.length&&<p>अद्याप कोणतीही प्रवेश विनंती, समस्या किंवा नोंदणी अर्ज नाही.</p>}
        </section>
      </div>
    </div>}


    {exemptionAccount && <div className="tax-modal-backdrop"><form className="tax-modal tax-form" onSubmit={submitExemption}><h2>कर सवलतीची विनंती</h2><p>{subjectLabel(exemptionAccount)} · {exemptionAccount.account_number}</p><label>सवलतीचा प्रकार<input name="exemption_type" required defaultValue="GOVERNMENT_APPROVED" /></label><div className="tax-form-row"><label>टक्केवारी<input name="percentage" type="number" min="0.0001" max="100" step="0.0001" /></label><label>किंवा निश्चित रक्कम<input name="fixed_amount" type="number" min="0.01" step="0.01" /></label></div><div className="tax-form-row"><label>पासून लागू<input name="valid_from" type="date" required /></label><label>पर्यंत लागू<input name="valid_to" type="date" /></label></div><label>कारण<textarea name="reason" required minLength="5" /></label><label>पुराव्याची नोंद<textarea name="evidence_note" /></label><div className="tax-actions"><button type="button" onClick={() => setExemptionAccount(null)}>रद्द करा</button><button className="tax-primary" disabled={busy}>विनंती सादर करा</button></div></form></div>}
    {appealDemand && <div className="tax-modal-backdrop"><form className="tax-modal tax-form" onSubmit={submitAppeal}><h2>{demandTaxName(appealDemand)} मागणीवर अपील</h2><p>{demandSubjectName(appealDemand)} · मागणी क्र. {appealDemand.demand_number}</p><label>कारण संकेत<input name="reason_code" required defaultValue="ASSESSMENT_ERROR" /></label><label>अपील निवेदन<textarea name="statement" required minLength="10" /></label><label>पुराव्याची नोंद<textarea name="evidence_note" /></label><label className="tax-check"><input name="stay_requested" type="checkbox" defaultChecked /> तपासणीदरम्यान वसुली स्थगित ठेवावी</label><div className="tax-actions"><button type="button" onClick={() => setAppealDemand(null)}>रद्द करा</button><button className="tax-primary" disabled={busy}>अपील सादर करा</button></div></form></div>}
    {receiptPreview && <TaxPdfViewer
      blob={receiptPreview.blob}
      fileName={receiptPreview.fileName}
      title={receiptPreview.title}
      onClose={() => setReceiptPreview(null)}
    />}
    {demandNoticePreview && <TaxPdfViewer
      blob={demandNoticePreview.blob}
      fileName={demandNoticePreview.fileName}
      title={demandNoticePreview.title}
      onClose={() => setDemandNoticePreview(null)}
    />}
  </section>;
}
